import type {ColDef, EditableCallbackParams, ICellRendererParams} from "ag-grid-community";
import {useTranslation} from "react-i18next";
import {NewPeakMatrix, PeakDetail} from "../../../../api/types/peakDetail";
import {useTheme} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import {snackbarTypes} from "../../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";

dayjs.extend(localeData)
dayjs().localeData()

export const useMatrixColDefs = ({
                                     matrixData,
                                     editable
                                 }: {
    matrixData: PeakDetail[];
    editable: boolean;
}): ColDef[] => {
    const {t} = useTranslation(["translation", "pricing"]);
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Theme
    const theme = useTheme();

    // Use State
    const [content, setContent] = useState<NewPeakMatrix | null>(null);

    // Use Effect
    useEffect(() => {
        if (matrixData) setContent(JSON.parse(JSON.stringify(matrixData)));
    }, [matrixData]);

    const cellValueSetter = (params: any) => {
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue > 100 && params.newValue < 0) {
            return false;
        }
        params.data[params.colDef.field.split(".")[0]] = params.newValue;
        return true;
    };

    const daysOfTheWeekUppercase = [
        {code: 0, label: dayjs().day(0).format("dddd").toUpperCase()},
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: -1, label: t('pricing:days.none').toUpperCase()},
    ];

    return [
        {
            headerName: t("pricing:peakMatrix.labels.locationPeak"),
            headerTooltip: t("pricing:peakMatrix.labels.locationPeak"),
            field: "location.name",
            flex: 1,
            editable: false,
            minWidth: 150,
            pinned: "left",
            sortable: false,
            cellRenderer: (params: ICellRendererParams<PeakDetail>) => params.data?.location.name
        },
        {
            headerName: t("pricing:peakMatrix.labels.peakDay"),
            headerTooltip: t("pricing:peakMatrix.labels.peakDay"),
            field: "peakDay",
            editable: editable,
            minWidth: 150,
            flex: 1,
            sortable: false,
            cellEditor: 'peakDaySelectEditor',
            cellEditorPopup: true,
            cellEditorPopupPosition: 'under',
            singleClickEdit: true,
            cellEditorParams: {
                values: daysOfTheWeekUppercase.map(d => d.label),
                cellHeight: 20,
                width: 250,
                formatValue: (value: number) => value,
                cellRenderer: (params: ICellRendererParams) =>
                    <div>{params.value}</div>
            },
        },
        {
            headerName: t("pricing:peakMatrix.labels.peakValue"),
            headerTooltip: t("pricing:peakMatrix.labels.peakValue"),
            field: "peakValue",
            editable: (params: EditableCallbackParams) => {
                return editable && params.data.peakDay !== t('pricing:days.none').toUpperCase();
            },
            minWidth: 150,
            valueSetter: cellValueSetter,
            cellEditor: "customEditor",
            flex: 1,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<PeakDetail>) => {
                return !params.data?.peakValue ? null : params.data?.peakValue + "%"
            }
        },
        {
            headerName: t("pricing:peakMatrix.labels.peakOffDay"),
            headerTooltip: t("pricing:peakMatrix.labels.peakOffDay"),
            field: "peakOffDay",
            editable: editable,
            minWidth: 150,
            flex: 1,
            sortable: false,
            cellEditor: 'peakDaySelectEditor',
            cellEditorPopup: true,
            cellEditorPopupPosition: 'under',
            singleClickEdit: true,
            onCellValueChanged: async (event) => {
                event.data.peakOffDay = event.newValue;
                if (event.newValue === t('pricing:days.none').toUpperCase()) {
                    if (event.data.peakDay === t('pricing:days.none').toUpperCase()) {
                        event.data.peakOffDay = event.oldValue;
                        dispatchGlobalData({
                            type: 'SET_SNACKBAR',
                            payload: {
                                snackbar: {
                                    message: t('pricing:peakMatrix.messages.peakDay&peakOffDayNotBeUndefined'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true,
                                }
                            }
                        });
                    } else {
                        event.data.peakOffValue = null;
                        event.api.refreshCells({columns: ["peakOffValue"]});
                    }
                } else if (event.data.peakDay === event.data.peakOffDay) {
                    event.data.peakOffDay = event.oldValue;
                    dispatchGlobalData({
                        type: 'SET_SNACKBAR',
                        payload: {
                            snackbar: {
                                message: t('pricing:peakMatrix.messages.peakDay&peakOffDayNotBeEqual'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
                event.api.refreshCells({columns: ["peakOffDay"]});
            },
            cellEditorParams: {
                values: daysOfTheWeekUppercase.map(d => d.label),
                cellHeight: 20,
                width: 250,
                formatValue: (value: string) => value,
                cellRenderer: (params: ICellRendererParams) =>
                    <div>{params.value}</div>
            },
        },
        {
            headerName: t("pricing:peakMatrix.labels.peakOffValue"),
            headerTooltip: t("pricing:peakMatrix.labels.peakOffValue"),
            field: "peakOffValue",
            editable: (params: EditableCallbackParams) => {
                return editable && params.data.peakOffDay !== t('pricing:days.none').toUpperCase();
            },
            minWidth: 150,
            valueSetter: cellValueSetter,
            cellEditor: "customEditor",
            flex: 1,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<PeakDetail>) => !params.data?.peakOffValue ? null : `-${params.data?.peakOffValue}%`
        },
    ];
};
